import Phaser from 'phaser';
import { GAME_W, setupCamera } from '../dpr.js';
import { GameStateManager } from '../state/GameStateManager';
import { RUNE_PACKS, RunePurchaseOverlay } from './RunePurchaseOverlay';
import { AuthService } from '../services/AuthService';

export class RuneShopScene extends Phaser.Scene {
  private gsm!: GameStateManager;
  private battleScene: (Phaser.Scene & { resumeBattle(): void }) | null = null;
  private fromPauseMenu = false;

  constructor() {
    super({ key: 'RuneShopScene' });
  }

  init(data: { gsm?: GameStateManager; battleScene?: any; fromPauseMenu?: boolean }): void {
    this.gsm = data.gsm || GameStateManager.getInstance();
    this.battleScene = data.battleScene || null;
    this.fromPauseMenu = data.fromPauseMenu ?? false;
  }

  create(): void {
    if (this.battleScene) {
      setupCamera(this, this.cameras.main, 'overlay');
      this.cameras.main.setBackgroundColor('rgba(5, 3, 5, 0.97)');
    } else {
      setupCamera(this, this.cameras.main, 'full');
      this.cameras.main.setBackgroundColor('#0a0608');
    }
    if (!this.gsm) this.gsm = GameStateManager.getInstance();

    const W = this.battleScene ? 480 : GAME_W;
    const auth = AuthService.getInstance();

    // Header
    this.add.rectangle(W / 2, 22, W, 44, 0x0a0806, 1).setDepth(10);
    this.add.rectangle(W / 2, 44, W, 1, 0x5a3600, 0.9).setDepth(10);
    this.add.text(W / 2, 22, 'Rune Shop', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    // Close button — ESC key mirrors it.
    const doClose = () => {
      if (this.fromPauseMenu) {
        this.scene.stop();
        this.scene.launch('MenuScene', { gsm: this.gsm, battleScene: this.battleScene });
      } else if (this.battleScene) {
        (this.battleScene as any).resumeBattle();
        this.scene.stop();
      } else {
        this.scene.start('MenuScene');
      }
    };
    const closeBtn = this.add.text(W - 20, 22, 'X', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(11);
    closeBtn.on('pointerover', () => closeBtn.setColor('#c8973a'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#5a3600'));
    closeBtn.on('pointerdown', doClose);
    this.input.keyboard?.once('keydown-ESC', doClose);

    // Rune balance
    const runesText = this.add.text(W / 2, 70, `${this.gsm.player.resources.loot ?? 0}`, {
      fontSize: '18px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5);

    // Rune packs
    const cardW = (W - 60) / 3;
    RUNE_PACKS.forEach((pack, i) => {
      const cx = 30 + cardW * i + cardW / 2;
      const cy = 160;

      this.add.rectangle(cx, cy, cardW - 8, 100, 0x0d0a0e, 0.97)
        .setStrokeStyle(1, 0x3a2050);

      this.add.text(cx, cy - 28, `${pack.runes}`, {
        fontSize: '18px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
      }).setOrigin(0.5);

      this.add.text(cx, cy + 4, `$${pack.priceUsd.toFixed(2)}`, {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: '#6b4020',
      }).setOrigin(0.5);

      const btnBg = this.add.rectangle(cx, cy + 30, 80, 24, 0x0a0a1a, 1)
        .setStrokeStyle(1, 0x3a3a6a).setInteractive({ useHandCursor: true });
      const btnTxt = this.add.text(cx, cy + 30, 'BUY', {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#9a9aca', fontStyle: 'bold',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      const doBuy = () => {
        const token = auth.getToken() ? `Bearer ${auth.getToken()}` : '';
        new RunePurchaseOverlay(token, (runes) => {
          this.gsm.player.resources.loot = (this.gsm.player.resources.loot ?? 0) + runes;
          this.gsm.save();
          runesText.setText(`${this.gsm.player.resources.loot}`);
        }).open(pack.id, pack.label, pack.priceUsd);
      };

      btnBg.on('pointerover', () => { btnTxt.setColor('#c8973a'); btnBg.setStrokeStyle(1, 0x9a6020); });
      btnBg.on('pointerout', () => { btnTxt.setColor('#9a9aca'); btnBg.setStrokeStyle(1, 0x3a3a6a); });
      btnBg.on('pointerdown', doBuy);
      btnTxt.on('pointerdown', doBuy);
    });
  }
}
